import * as React from "react"
import {observer} from "mobx-react"
import {Drawer, Icon, List, ListItem} from "@cling/lib.web.mdc"
import {report_error} from "@cling/lib.shared.debug"
import {i18n} from "@cling/lib.web.i18n"
import {current_user, import_board_state, ui_actions} from "../state/index"
import {
    as_language,
    GetUploadTempURLRequest,
    GetUploadTempURLResponse,
    whats_new_board_uid,
} from "@cling/lib.shared.model"
import {let_user_select_files, upload_files} from "../upload/upload"
import {call_function} from "@cling/lib.shared.faas"
import {LIMIT_BOARD_PACKAGE_SIZE} from "@cling/lib.shared.limits"
import {PrincipalInfo} from "../account/principal_info"
import {BrowserExtLink} from "../misc/browser_ext_link"
import {logout as auth_logout} from "@cling/lib.web.auth"
import {ListDivider} from "@cling/lib.web.mdc/list"
import {logout as cling_button_logout} from "../cling_button_gateway"
import {QuotaOverview} from "../payment/quota_overview"
import {board_info_resource, board_resource, team_resource} from "@cling/lib.web.resources"
import {goto_board} from "../utils"
import {running_on_mobile_device} from "@cling/lib.web.utils"
import {board_quota_limits} from "@cling/lib.shared.model/quota"
import {open_intercom} from "../misc/intercom"
import {navigate} from "@reach/router/index"
import {simple_tooltip_event_handlers} from "@cling/lib.web.mdc/simple_tooltip"

interface Props {
    header_style?: React.CSSProperties
    close: () => void
    is_open: boolean
    onOpen?: () => void
    onClose?: () => void
}

export const BoardDrawer = observer(({header_style, is_open, close, onOpen, onClose}: Props) => {
    // eslint-disable-next-line @eslint-react/no-create-ref
    const drawer_ref = React.createRef<Drawer>()
    const [clicks_on_version_counter, set_clicks_on_version_counter] = React.useState(0)
    React.useEffect(() => {
        if (is_open) {
            drawer_ref.current?.open()
        } else {
            drawer_ref.current?.close()
        }
    }, [drawer_ref, is_open])
    const open_account_settings_dialog = React.useCallback(() => {
        close()
        ui_actions.open_account_settings_dialog()
    }, [close])
    const logout = React.useCallback(() => {
        cling_button_logout()
        auth_logout().catch(report_error)
    }, [])
    const open_teams_dialog = React.useCallback(() => {
        close()
        ui_actions.open_teams_dialog()
    }, [close])
    const goto_organizations_page = React.useCallback(() => {
        close()
        navigate("/c/organizations").catch(report_error)
    }, [close])
    const open_cling_website = React.useCallback(() => {
        close()
        window.open(`/${as_language(current_user.account.locale)}/index.html`, "_blank")
    }, [close])
    const open_privacy = React.useCallback(() => {
        close()
        window.open(`/${as_language(current_user.account.locale)}/privacy.html`, "_blank")
    }, [close])
    const open_terms = React.useCallback(() => {
        close()
        window.open(`/${as_language(current_user.account.locale)}/terms.html`, "_blank")
    }, [close])
    const open_about_cling = React.useCallback(() => {
        close()
        window.open(`/${as_language(current_user.account.locale)}/about.html`, "_blank")
    }, [close])
    const import_board = React.useCallback(async () => {
        close()
        const files = await let_user_select_files({accept: ".zip"})
        if (!files.length) {
            return
        }
        upload_files({
            files: files.map((x) => ({file: x})),
            file_size_limit: LIMIT_BOARD_PACKAGE_SIZE,
            get_upload_url: async (o: {mime_type: string; md5_base64: string; size: number}) =>
                call_function(new GetUploadTempURLRequest(o), GetUploadTempURLResponse),
            on_file_uploaded: ({upload_id}) => import_board_state.start_import(upload_id),
        })
    }, [close])
    const open_help = React.useCallback(() => {
        close()
        open_intercom("home")
    }, [close])
    const goto_whats_new_board = React.useCallback(() => {
        const board_info = board_info_resource.read(
            whats_new_board_uid(current_user.account.locale),
        )
        if (!board_info) {
            return
        }
        close()
        goto_board({board_uid: board_info.uid}).catch(report_error)
    }, [close])
    const on_click_on_version = React.useCallback(() => {
        // Open diagnostics dialog on third click ...
        if (clicks_on_version_counter === 2) {
            ui_actions.open_diagnostics_dialog()
            set_clicks_on_version_counter(0)
        } else {
            set_clicks_on_version_counter(clicks_on_version_counter + 1)
        }
    }, [clicks_on_version_counter])
    const can_import_board = current_user.global_permissions.can_import_board || window._under_test
    const {account} = current_user
    const visible_team_uids = current_user.visible_team_memberships
    const visible_teams =
        visible_team_uids.length > 0
            ? `${i18n.member_of}: ${visible_team_uids
                  .map((x) => team_resource.read(x)?.name || "...")
                  .join(", ")}`
            : undefined
    const header = (
        <div className="board-drawer__header" onClick={close}>
            <div className="board-drawer__header-spacer" />
            <div
                className="board-drawer__header-user"
                aria-label={visible_teams}
                {...simple_tooltip_event_handlers}
            >
                <div className="board-drawer__header-avatar">
                    <PrincipalInfo display="avatar_large" account={account} />
                </div>
                <div className="board-drawer__header-username">
                    <PrincipalInfo display="full_name" account={account} />
                    <div className="board-drawer__header-email">
                        {current_user.account_attributes.email}
                    </div>
                </div>
            </div>
        </div>
    )
    const whats_new_board_info = board_info_resource.read(
        whats_new_board_uid(current_user.account.locale),
    )
    const usage = board_resource.board_quota_usage
    const limits = board_quota_limits(current_user.account_attributes)
    const show_condensed_pro_teaser =
        usage.num_boards / limits.num_boards < 0.9 &&
        usage.num_cards / limits.num_cards < 0.7 &&
        usage.sum_blob_size / limits.sum_blob_size < 0.7
    const has_visible_teams = visible_team_uids.length > 0
    const is_organization_admin = current_user.account_attributes.admin_organization_uids.length > 0
    return (
        <Drawer
            className="board-drawer"
            ref={drawer_ref}
            header={header}
            header_style={header_style}
            focus_first_list_item_on_open={!running_on_mobile_device()}
            onOpen={onOpen}
            onClose={onClose}
        >
            <div>
                <List>
                    {!current_user.account_attributes.has_pro_features &&
                        // Only needed because of Obersee.
                        current_user.account_attributes.team_uids.length == 0 && (
                            <QuotaOverview
                                title={show_condensed_pro_teaser ? "" : i18n.your_cling}
                                className="board-drawer__quota-overview"
                                on_checkout_dialog_open={close}
                                hide_usage_counters={show_condensed_pro_teaser}
                                hide_tip={show_condensed_pro_teaser}
                            />
                        )}
                    <ListItem
                        onClick={open_account_settings_dialog}
                        data-test-id="BoardDrawer_settings"
                    >
                        <Icon icon="settings" outlined />
                        {i18n.settings}
                    </ListItem>
                    <ListItem onClick={open_help}>
                        <Icon icon="chat" outlined />
                        {i18n.get_help}
                    </ListItem>
                    {whats_new_board_info && (
                        <ListItem onClick={goto_whats_new_board}>
                            <Icon outlined icon="notifications" />
                            {i18n.whats_new}
                        </ListItem>
                    )}
                    <ListItem onClick={open_cling_website}>
                        <Icon icon="home" outlined />
                        {i18n.open_website}
                    </ListItem>
                    <BrowserExtLink type="ListItem" onClick={close} />
                    {can_import_board && (
                        <ListItem onClick={import_board} data-test-id="BoardDrawer_import_board">
                            <Icon icon="file_upload" outlined />
                            {i18n.import_board}
                        </ListItem>
                    )}
                    <ListDivider />
                    {is_organization_admin && (
                        <ListItem
                            onClick={goto_organizations_page}
                            data-test-id="BoardDrawer_organizations"
                        >
                            <Icon icon="groups" outlined />
                            {i18n.organizations}
                        </ListItem>
                    )}
                    {has_visible_teams && (
                        <ListItem onClick={open_teams_dialog}>
                            <Icon icon="group" outlined />
                            {i18n.my_teams}
                        </ListItem>
                    )}
                    <ListDivider />
                    <ListItem onClick={open_privacy}>
                        <Icon icon="privacy_tip" outlined />
                        {i18n.privacy}
                    </ListItem>
                    <ListItem onClick={open_terms}>
                        <Icon icon="subject" outlined />
                        {i18n.terms_of_use}
                    </ListItem>
                    <ListItem onClick={open_about_cling}>
                        <Icon icon="info" outlined />
                        {i18n.imprint}
                    </ListItem>
                    <ListDivider />
                    <ListItem onClick={logout} data-test-id="BoardDrawer_logout">
                        <Icon icon="exit_to_app" outlined />
                        {i18n.log_out}
                    </ListItem>
                </List>
            </div>
            <div className="board-drawer__version" onClick={on_click_on_version}>
                {cling.version}
            </div>
        </Drawer>
    )
})
